// Billing domain — the advertiser ledger + balance. Shared by the backend
// (models/TransactionModel + the billing routes) and the advertiser portal.
// Schemas are authoritative; the TS types are derived via z.infer<>.

import { z } from 'zod';

export const TransactionTypeSchema = z.enum([
  'deposit',
  'impression_debit',
  // A recorded ad click. The platform bills CPM-only (per impression); clicks are a FREE
  // engagement signal, so a `click` row always carries amountCents: 0 and never moves the
  // balance — it exists solely so per-campaign click counts survive in spend analytics.
  // (CPC billing was removed 2026-08-28; this was formerly the billed `click_debit`.)
  'click',
  // Stripe money movements after a deposit. A `refund` returned money to the advertiser's card and
  // a `chargeback` is their bank taking a payment back: both debit the balance. A `reversal`
  // credits one of them back when it did not happen after all (a refund that failed, a chargeback
  // the platform won).
  'refund',
  'chargeback',
  'reversal',
  'adjustment',
]);
export type TransactionType = z.infer<typeof TransactionTypeSchema>;

export const TransactionMetadataSchema = z.object({
  campaignId: z.string().optional(),
  adId: z.string().optional(),
  // Impressions represented by an impression_debit row. Impressions are billed per 1000 (one
  // CPM charge), so a single debit row stands for this many impressions — the advertiser ledger
  // shows the count, and spend analytics sums it rather than counting rows.
  impressions: z.number().optional(),
});
export type TransactionMetadata = z.infer<typeof TransactionMetadataSchema>;

/**
 * A ledger entry on an advertiser's balance as the portal reads it (ISO string `createdAt`). The
 * backend spreads its fuller `TransactionDocument` (models/TransactionModel) into the response —
 * `advertiserId` and the Stripe refs come along — and this schema names the subset the portal uses.
 */
export const TransactionSchema = z.object({
  id: z.string(),
  type: TransactionTypeSchema,
  amountCents: z.number(),
  balanceAfterCents: z.number(),
  description: z.string(),
  metadata: TransactionMetadataSchema.optional(),
  createdAt: z.string(),
});
export type Transaction = z.infer<typeof TransactionSchema>;

/** Advertiser account balance — cents canonical, dollars pre-formatted for display. */
export const AdvertiserBalanceSchema = z.object({
  balanceCents: z.number(),
  balanceDollars: z.string(),
});
export type AdvertiserBalance = z.infer<typeof AdvertiserBalanceSchema>;
